import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Check,
  ArrowUpDown,
  Loader2,
  Sparkles,
  Info,
  RefreshCw
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

interface BenefitAttributeMapping {
  id: string;
  sector_id: number;
  benefit_name: string;
  benefit_description: string;
  attribute_topic_id: string | null;
  attribute_name: string;
  attribute_topic_name: string | null;
  topic_description: string | null;
  importance_weight: number;
  direction: 'higher_better' | 'lower_better' | 'specific_values' | 'any';
  target_values: string[] | null;
  notes: string | null;
  created_at: string;
}

interface Benefit {
  benefit_name: string;
  benefit_description: string | null;
}

interface AttributeValueRange {
  id: string;
  attribute_name: string;
  product_category: string | null;
  data_type: string;
  min_value: number | null;
  max_value: number | null;
  typical_range_min: number | null;
  typical_range_max: number | null;
  unit: string | null;
  quality_tiers: Record<string, any> | null;
  canonical_values: string[] | null;
}

const directionIcons: Record<string, React.ReactNode> = {
  higher_better: <TrendingUp className="h-4 w-4" />,
  lower_better: <TrendingDown className="h-4 w-4" />,
  specific_values: <Target className="h-4 w-4" />,
  any: <Check className="h-4 w-4" />,
};

const directionColors: Record<string, string> = {
  higher_better: 'bg-green-100 text-green-800',
  lower_better: 'bg-blue-100 text-blue-800',
  specific_values: 'bg-purple-100 text-purple-800',
  any: 'bg-gray-100 text-gray-800',
};

const directionLabels: Record<string, string> = {
  higher_better: 'Higher is Better',
  lower_better: 'Lower is Better',
  specific_values: 'Specific Values',
  any: 'Any Value',
};

export default function BenefitAttributeMappings() {
  const { selectedSite } = useSite();
  const [mappings, setMappings] = useState<BenefitAttributeMapping[]>([]);
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [valueRanges, setValueRanges] = useState<AttributeValueRange[]>([]);
  const [selectedBenefit, setSelectedBenefit] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Get sector ID from selected site
  const sectorId = selectedSite?.sector_id || 1;

  useEffect(() => {
    if (sectorId) {
      loadData();
    }
  }, [sectorId]);

  useEffect(() => {
    if (selectedBenefit && sectorId) {
      loadMappings();
    }
  }, [selectedBenefit, sectorId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBenefits(),
        loadMappings(),
        loadValueRanges(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load mapping data',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBenefits = async () => {
    try {
      const response = await authFetch(`${API_URL}/mappings/benefits/${sectorId}`);
      if (!response.ok) throw new Error('Failed to load benefits');
      const data = await response.json();
      setBenefits(data.benefits || []);
    } catch (error) {
      console.error('Error loading benefits:', error);
    }
  };

  const loadMappings = async () => {
    try {
      const url = selectedBenefit === 'all'
        ? `${API_URL}/mappings/benefit-attributes/${sectorId}`
        : `${API_URL}/mappings/benefit-attributes/${sectorId}?benefit=${encodeURIComponent(selectedBenefit)}`;

      const response = await authFetch(url);
      if (!response.ok) throw new Error('Failed to load mappings');
      const data = await response.json();
      setMappings(data.mappings || []);
    } catch (error) {
      console.error('Error loading mappings:', error);
    }
  };

  const loadValueRanges = async () => {
    try {
      const response = await authFetch(`${API_URL}/mappings/value-ranges/${sectorId}`);
      if (!response.ok) throw new Error('Failed to load value ranges');
      const data = await response.json();
      setValueRanges(data.ranges || []);
    } catch (error) {
      console.error('Error loading value ranges:', error);
    }
  };

  const handleAutoPopulate = async () => {
    setPopulating(true);
    setMessage(null);
    try {
      const response = await authFetch(`${API_URL}/mappings/populate/${sectorId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) throw new Error('Failed to auto-populate mappings');
      const data = await response.json();

      setMessage({
        type: 'success',
        text: `Successfully auto-populated mappings for sector ${sectorId}`,
      });

      // Reload all data
      await loadData();
    } catch (error) {
      console.error('Error auto-populating:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to auto-populate mappings',
      });
    } finally {
      setPopulating(false);
    }
  };

  const getValueRangeForAttribute = (attributeName: string): AttributeValueRange | undefined => {
    return valueRanges.find(r => r.attribute_name === attributeName);
  };

  const formatWeightPercentage = (weight: number): string => {
    return `${Math.round(weight * 100)}%`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Benefit-Attribute Mappings</h1>
          <p className="text-muted-foreground">
            View and manage how product attributes map to customer benefits
          </p>
        </div>
        <Button
          onClick={handleAutoPopulate}
          disabled={populating || !selectedSite}
          className="gap-2"
        >
          {populating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Auto-Populating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Auto-Populate with AI
            </>
          )}
        </Button>
      </div>

      {/* Message Alert */}
      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Mappings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mappings.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Unique Benefits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{benefits.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Value Ranges Defined</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{valueRanges.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter mappings by benefit type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Select value={selectedBenefit} onValueChange={setSelectedBenefit}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a benefit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Benefits</SelectItem>
                  {benefits.map((benefit) => (
                    <SelectItem key={benefit.benefit_name} value={benefit.benefit_name}>
                      {benefit.benefit_name}
                      {benefit.benefit_description && (
                        <span className="text-muted-foreground ml-2">
                          - {benefit.benefit_description}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedBenefit === 'all'
              ? 'All Benefit-Attribute Mappings'
              : `"${selectedBenefit}" Mappings`
            }
          </CardTitle>
          <CardDescription>
            {mappings.length} mapping{mappings.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No mappings found</p>
              <p className="text-sm mt-2">
                Try auto-populating with AI to generate mappings
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benefit</TableHead>
                    <TableHead>Attribute</TableHead>
                    <TableHead>Weight</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead>Target Values</TableHead>
                    <TableHead>Value Range</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.map((mapping) => {
                    const valueRange = getValueRangeForAttribute(mapping.attribute_name);
                    return (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{mapping.benefit_name}</div>
                            {mapping.benefit_description && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {mapping.benefit_description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{mapping.attribute_name}</div>
                            {mapping.attribute_topic_name && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Topic: {mapping.attribute_topic_name}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {formatWeightPercentage(mapping.importance_weight)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={directionColors[mapping.direction]}>
                            <span className="flex items-center gap-1">
                              {directionIcons[mapping.direction]}
                              {directionLabels[mapping.direction]}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {mapping.target_values && mapping.target_values.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {mapping.target_values.map((value, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {value}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {valueRange ? (
                            <div className="text-xs">
                              {valueRange.min_value !== null && valueRange.max_value !== null ? (
                                <div>
                                  {valueRange.min_value} - {valueRange.max_value}
                                  {valueRange.unit && ` ${valueRange.unit}`}
                                </div>
                              ) : (
                                <div className="text-muted-foreground">No range</div>
                              )}
                              {valueRange.canonical_values && valueRange.canonical_values.length > 0 && (
                                <div className="text-muted-foreground mt-1">
                                  Values: {valueRange.canonical_values.slice(0, 3).join(', ')}
                                  {valueRange.canonical_values.length > 3 && '...'}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping.notes ? (
                            <div className="text-xs text-muted-foreground max-w-xs truncate">
                              {mapping.notes}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
